import React, { useEffect, useState } from "react";
import { FleetrifyModal } from "../../../Modal/modal";
import { Eye, EyeOff } from "lucide-react";
import FleetrifyPasswordChecker from "../../../Password/password-checker";
import FleetrifyPasswordMatch from "../../../Password/password-match";
import Skeleton from "react-loading-skeleton";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  useAcceptInviteMutation,
  useGetHeadOrgInviteQuery,
} from "../../../../redux/services/companyApi";
import { FLEETRIFYROUTEPATH } from "../../../../routes/routesPath";
import { FleetrifyInputField } from "../../../Input/input";
import { getApiErrorMessage } from "../../../../utils/apiError";

const AcceptUserInviteModal = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // get invite id from url
  const id = searchParams.get("id");

  // form state
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [acceptInvite, { isLoading }] = useAcceptInviteMutation();

  // fetch head-of-org invite details
  const {
    data: userData,
    isLoading: isUserLoading,
  // } = useGetHeadOrgInviteQuery(id ?? skipToken);
  } =useGetHeadOrgInviteQuery(id || skipToken);

  // prefill form when data loads
  useEffect(() => {
    if (userData) {
      setFirstname(userData.firstname || "");
      setLastname(userData.lastname || "");
      setEmail(userData.email || "");
    }
  }, [userData]);

  const isDisabled =
    !password || !confirmPassword || password !== confirmPassword;

  const handleAcceptUser = async () => {
    if (!id) return;

    try {
      await acceptInvite({
        id,
        password,
        password_confirmation: confirmPassword,
      }).unwrap();

      toast.success("Invitation accepted successfully!", {
        style: {
          background: "#e0f2fe",
          color: "#1E40AF",
          border: "1px solid #1E40AF",
        },
      });

      navigate(FLEETRIFYROUTEPATH.LOGIN);
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, "Failed to accept invite. Try again."), {
        style: {
          background: "#ffe5e5",
          color: "#ef4444",
          border: "1px solid #ff4d4f",
        },
      });
    }
  };

  return (
    <FleetrifyModal
      disableClose
      isOpen={true}
      className="max-w-2xl max-h-[85vh] hide-scrollbar"
      childrenClassName="flex flex-col pb-0 overflow-hidden"
      overlayClassName="z-[9999]"
      enableOutsideClick={false}
    >
      <div className="flex flex-col flex-1 min-h-0 px-10 mt-10">
        {/* header */}
        <div className="space-y-5">
          <h1 className="text-[#00032A] text-[20px] font-bold">
            Dear{" "}
            {isUserLoading ? (
              <Skeleton width={120} height={20} />
            ) : (
              <span className="font-extrabold capitalize ">
                {firstname} {lastname}
              </span>
            )}
          </h1>
          <p className="text-[#00032A]">
            Please set your password to complete the invite acceptance.
          </p>
        </div>

        {/* form */}
        <div className="flex-1 min-h-0 overflow-y-auto mt-2 hide-scrollbar space-y-6 py-6">
          <FleetrifyInputField
            label="First Name"
            id="firstname"
            value={firstname}
            disabled
            onChange={() => {}}
          />

          <FleetrifyInputField
            label="Last Name"
            id="lastname"
            value={lastname}
            disabled
            onChange={() => {}}
          />

          <FleetrifyInputField
            label="Email Address"
            id="email"
            value={email}
            disabled
            onChange={() => {}}
          />

          <FleetrifyInputField
            type={showPassword ? "text" : "password"}
            label="New Password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="***********************"
            icon={
              <button type="button" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            }
          />

          <FleetrifyPasswordChecker password={password} />

          <FleetrifyInputField
            type={showConfirmPassword ? "text" : "password"}
            label="Confirm Password"
            id="confirm_password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="***********************"
            icon={
              <button
                type="button"
                onClick={() =>
                  setShowConfirmPassword(!showConfirmPassword)
                }
              >
                {showConfirmPassword ? (
                  <EyeOff size={16} />
                ) : (
                  <Eye size={16} />
                )}
              </button>
            }
          />

          <FleetrifyPasswordMatch password={password} confirmPassword={confirmPassword} />
        </div>

        {/* footer */}
        <div className="sticky bottom-0 bg-white py-4 mt-6 flex justify-end border-t border-gray-200">
          <button
            onClick={handleAcceptUser}
            disabled={isDisabled || isLoading || isUserLoading || !id}
            className={`px-10 py-2 rounded-md transition-colors ${
              isDisabled || isLoading || isUserLoading || !id
                ? "bg-blue-400 text-white cursor-not-allowed"
                : "bg-[#1E40AF] hover:bg-blue-800 text-white"
            }`}
          >
            {isLoading ? "Processing..." : "Continue"}
          </button>
        </div>
      </div>
    </FleetrifyModal>
  );
};

export default AcceptUserInviteModal;
